import { useAuthenticated, useFloors } from "@gettactic/hooks";
import React from "react";
import { IconShape, ZoneLink } from "../../../../../../types";
import { EditorField } from "../../../../../ui/EditorField";
import { EditorSelect } from "../../../../../ui/EditorSelect";
import { PanelSection } from "../../../../../ui/PanelShell";
import { Seg } from "../../../../../ui/Seg";
import { useMapEditorActions } from "../../../../contexts/MapEditorContext";
import { ShapeSectionOptions } from "../ShapePanel";

// Radix Select items cannot use an empty-string value, so "(none)" gets a
// sentinel that maps back to `undefined` on change.
const NO_CATEGORY_VALUE = "__none__";

/**
 * Optional click-to-navigate link for icon shapes — same ZoneLink model
 * and navigation mechanism zones use (office map or external URL), kept
 * out of the legend entirely: it only surfaces as a hover tooltip on the
 * icon itself in the viewer.
 */
export const IconLinkPanelControls: React.FC<ShapeSectionOptions> = ({
  shapes
}) => {
  const { updateShape } = useMapEditorActions();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const iconShapes = shapes.filter(
    (shape): shape is IconShape => shape.type === "icon"
  );
  const shape = iconShapes[0];

  const offices = authenticatedUser?.offices?.offices || [];

  const linkOfficeId =
    shape?.link?.type === "office" ? shape.link.officeId || null : null;
  const { floors: linkOfficeFloors } = useFloors(linkOfficeId);

  if (!shape) return null;

  const handleLinkTypeChange = (type: ZoneLink["type"] | "none") => {
    iconShapes.forEach((s) => {
      const current = s.link;
      if (type === "none") {
        updateShape(s.id, { link: undefined } as Partial<IconShape>);
      } else if (type === "office") {
        updateShape(s.id, {
          link: {
            type: "office",
            officeId: current?.officeId,
            label: current?.label,
            description: current?.description
          }
        } as Partial<IconShape>);
      } else {
        updateShape(s.id, {
          link: {
            type: "url",
            url: current?.url || "",
            description: current?.description
          }
        } as Partial<IconShape>);
      }
    });
  };

  const handleLinkOfficeChange = (officeId: string) => {
    if (officeId === NO_CATEGORY_VALUE) return;
    const office = offices.find((o) => o.id === officeId);
    iconShapes.forEach((s) => {
      const current = s.link;
      updateShape(s.id, {
        link: {
          ...current,
          type: "office",
          officeId,
          label: office?.name,
          // Changing office invalidates the floor — floors belong to an office.
          floorId: undefined
        }
      } as Partial<IconShape>);
    });
  };

  const handleLinkFloorChange = (floorId: string) => {
    iconShapes.forEach((s) => {
      const current = s.link;
      if (current?.type !== "office") return;
      updateShape(s.id, {
        link: {
          ...current,
          floorId: floorId === NO_CATEGORY_VALUE ? undefined : floorId
        }
      } as Partial<IconShape>);
    });
  };

  const handleLinkUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    iconShapes.forEach((s) => {
      updateShape(s.id, {
        link: { ...s.link, type: "url", url: e.target.value }
      } as Partial<IconShape>);
    });
  };

  const handleLinkDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    iconShapes.forEach((s) => {
      if (!s.link) return;
      updateShape(s.id, {
        link: { ...s.link, description: e.target.value }
      } as Partial<IconShape>);
    });
  };

  return (
    <PanelSection label="Link">
      <div className="flex flex-col gap-2">
        <Seg<ZoneLink["type"] | "none">
          label="Link"
          value={shape.link?.type || "none"}
          onValueChange={(value) => {
            if (value === (shape.link?.type || "none")) return;
            handleLinkTypeChange(value);
          }}
          options={[
            { value: "none", ariaLabel: "None", label: "None" },
            { value: "office", ariaLabel: "Office Map", label: "Office" },
            { value: "url", ariaLabel: "External URL", label: "URL" }
          ]}
        />
        {shape.link?.type === "office" ? (
          <EditorSelect
            label="Office"
            value={shape.link.officeId || NO_CATEGORY_VALUE}
            onValueChange={handleLinkOfficeChange}
            options={[
              { value: NO_CATEGORY_VALUE, label: "(choose office)" },
              ...offices.map((office) => ({
                value: office.id,
                label: office.name
              }))
            ]}
          />
        ) : null}
        {shape.link?.type === "office" && shape.link.officeId ? (
          <EditorSelect
            label="Floor"
            value={shape.link.floorId || NO_CATEGORY_VALUE}
            onValueChange={handleLinkFloorChange}
            options={[
              { value: NO_CATEGORY_VALUE, label: "(default floor)" },
              ...(linkOfficeFloors || []).map((linkFloor) => ({
                value: linkFloor.id,
                label: linkFloor.name || linkFloor.id
              }))
            ]}
          />
        ) : null}
        {shape.link?.type === "url" ? (
          <EditorField
            label="URL"
            type="text"
            value={shape.link.url || ""}
            onChange={handleLinkUrlChange}
            placeholder="https://…"
          />
        ) : null}
        {shape.link ? (
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">
              Description (optional)
            </span>
            <textarea
              aria-label="Description"
              value={shape.link.description || ""}
              onChange={handleLinkDescriptionChange}
              placeholder="Shown in a popover before the link opens…"
              rows={3}
              className="w-full resize-none rounded-md border border-neutral-200 bg-background-primary px-2 py-1.5 text-neutral-900 text-xs outline-none transition-colors placeholder:text-neutral-400 focus:border-blue-500 hover:border-neutral-300"
            />
          </div>
        ) : null}
        {shape.link ? (
          <p className="text-neutral-500 text-xs">
            {shape.link.description
              ? "Viewers see the description in a popover with a button to open the link."
              : `Viewers can click this icon to open the linked ${
                  shape.link.type === "office" ? "office map" : "URL"
                }.`}{" "}
            The link never appears in the map legend.
          </p>
        ) : null}
      </div>
    </PanelSection>
  );
};

IconLinkPanelControls.displayName = "IconLinkPanelControls";
